import { useStream } from '@laravel/stream-vue';
import { computed, onScopeDispose, ref, watch } from 'vue';
import { useAccountGate } from '@/composables/useAccountGate';
import { usePwa } from '@/composables/usePwa.js';


function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export function useAiStream() {
  const { guard } = useAccountGate();
  const targetContent = ref('');
  const token = getCookie('XSRF-TOKEN');
  const decodedToken = decodeURIComponent(token); // CRITICAL: Decode the token!
  // Initialize useStream with dynamic options
  const {
    data,
    isFetching,
    isStreaming,
    send,
    cancel: cancelStream,
  } = useStream(`${import.meta.env.VITE_API_BASE_URL}/api/v1/enhance-content/stream`, {

    credentials: 'include',
    headers: { 'X-XSRF-TOKEN': decodedToken },
    onData: (chunk) => {
      // Optional: Process each chunk
      targetContent.value += chunk;
    },
    onFinish: () => {
      // Stream completed
      console.log('Stream completed');
    },
    onError: (error) => {
      console.error('Stream error:', error);
    },
  });

  // Track the most recent watcher cleanup so onScopeDispose can stop it
  // alongside cancelling the in-flight stream. Without this, navigating
  // away from a non-keepAlive page mid-stream leaks the EventSource.
  let unwatchRef = null;

  // ── PWA update-prompt deferral ──────────────────────────────────────────
  // While a stream is active, hold the SW update prompt so a `Reload` click
  // in App.vue can't kill the in-flight EventSource. Released as soon as
  // `useStream` flips both `isFetching` and `isStreaming` back to false, or
  // on scope dispose (page navigation).
  // PWA playbook Section 3 "Update during AI stream"; Section 5 row 6; PWA audit MEDIUM-4.
  const isAnyStreamActivity = computed(() => isFetching.value || isStreaming.value);
  const { holdReload } = usePwa();
  let releaseReloadHold = null;

  watch(isAnyStreamActivity, (busy) => {
    if (busy && !releaseReloadHold) {
      releaseReloadHold = holdReload('ai-stream');
    } else if (!busy && releaseReloadHold) {
      releaseReloadHold();
      releaseReloadHold = null;
    }
  });

  async function startStream(targetRef, sourceContent) {
    if (!guard('aiEnhance')) return;
    if (!targetRef) {
      throw new Error('Target ref is required');
    }

    const content = typeof sourceContent === 'function'
      ? sourceContent()
      : sourceContent;

    // Reset state
    targetRef.value = '';
    targetContent.value = '';

    // Send content to stream
    await send({ postId: content });

    // Stop a previous-call watcher if startStream is invoked twice
    unwatchRef?.();

    // Watch for data updates and sync to targetRef
    const unwatch = watch(data, (newData) => {
      targetRef.value = newData;
    }, { immediate: true });

    unwatchRef = unwatch;

    // Return cleanup function
    return () => {
      unwatch();
      if (unwatchRef === unwatch) unwatchRef = null;
    };
  }

  function cancel() {
    cancelStream();
  }

  onScopeDispose(() => {
    cancelStream();
    releaseReloadHold?.();
    releaseReloadHold = null;
    unwatchRef?.();
    unwatchRef = null;
  });

  return {
    startStream,
    cancel,
    // Return the same computed used to drive holdReload so consumers and the
    // reload-hold registry observe identical truth.
    isStreaming: isAnyStreamActivity,
    error: computed(() => null), // Error handling via onError callback
  };
}
